type MarketplacePresentation = {
  color: string
  notice?: string
  hideCommissionSection?: boolean
  /**
   * The marketplace is **withheld**: it is absent from the channel comparator and the
   * fallback selection, so nothing in the app prices it. The home picker still shows it as
   * an inert "Em breve" card, last in the grid. Set while the
   * marketplace is still on the shared `commissionOnlyAdapter` and awaiting its own adapter
   * that models the channel's real fee structure.
   *
   * The comparator is the one place a teaser would do harm: a marketplace the app refuses
   * to open a calculator for would appear in a leaderboard with a price beside it.
   *
   * Its detail page still resolves, still carries its editorial content and is still in the
   * sitemap; `MarketplaceComingSoon` swaps the calculator for a teaser there. Someone
   * arriving from search sees an honest "em breve" instead of a 404, which is the one place
   * the old behaviour was right.
   *
   * Going live is still a one-line change: delete this flag once `<id>/adapter.ts` and
   * `<id>/data.ts` model the channel properly.
   */
  comingSoon?: boolean
}

const PRESENTATION: Record<string, MarketplacePresentation> = {
  generic: {
    color: '#12D878',
    hideCommissionSection: true,
  },
  mercadolivre: {
    color: '#FFFFFF',
    notice:
      'Envios: referência para produto novo em Full, Coleta ou Agências, com reputação verde, MercadoLíder ou sem reputação. Confirme as exceções e a comissão no seu anúncio.',
  },
  shopee: {
    color: '#EE4D2D',
    hideCommissionSection: true,
  },
  magalu: {
    color: '#0086FF',
    notice: 'Condições promocionais dependem de elegibilidade e período.',
  },
  shein: {
    color: '#FFFFFF',
    notice: 'A comissão pode variar por campanha, categoria e contrato.',
  },
  tiktok: {
    color: '#25F4EE',
    notice:
      'A tarifa é aplicada automaticamente conforme a faixa de preço vigente.',
  },
  aliexpress: {
    color: '#FF4747',
    notice:
      'Use a taxa exibida no contrato da categoria e considere logística e câmbio.',
  },
  amazon: {
    color: '#FF9900',
    notice:
      'A comissão varia por categoria; mensalidade e logística entram como custos adicionais.',
  },
  kwai: {
    color: '#FF5A19',
    notice: 'Condições comerciais podem variar por seller e campanha.',
  },
  temu: {
    color: '#FF6A00',
    notice: 'Valide impostos e as condições comerciais da sua conta.',
  },
  airbnb: {
    color: '#FF385C',
    notice: 'A taxa depende da estrutura escolhida e do tipo de anfitrião.',
    comingSoon: true,
  },
  ifood: {
    color: '#EA1D2C',
    notice:
      'Pagamento online, mensalidade e contrato podem gerar cobranças adicionais.',
    comingSoon: true,
  },
  uairango: {
    color: '#F47A20',
    hideCommissionSection: true,
    comingSoon: true,
  },
}

export function marketplacePresentation(id: string): MarketplacePresentation {
  return (
    PRESENTATION[id] ?? {
      color: '#12D878',
      notice: 'Confirme sempre as tarifas e condições da sua conta.',
    }
  )
}

/** Whether this marketplace is withheld: a teaser card in the picker, absent from the comparator. */
export function isComingSoon(id: string): boolean {
  return marketplacePresentation(id).comingSoon === true
}

/**
 * The marketplaces the app actually offers.
 *
 * One filter feeding the comparator and the fallback selection, so those can never
 * disagree about what can be priced. The picker deliberately takes the full list and
 * renders withheld marketplaces as inert "Em breve" cards — announced, never ranked.
 */
export function offeredMarketplaces<T extends { id: string }>(
  marketplaces: ReadonlyArray<T>,
): Array<T> {
  return marketplaces.filter((option) => !isComingSoon(option.id))
}
